import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const faqs = [
  {
    question: "I'm a little bit confused. Which plan is correct for me?",
    answer: <>No worries! Join our <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Discord community</a> and head to the <span className="text-purple-400">'#open-a-ticket'</span> channel. There, you can explain your needs and we'll guide you to the best plan for your situation.</>,
  },
  {
    question: "What's the difference between a relay (endpoint) and OBS hosting?",
    answer: <>
      <span className="text-cyan-400 font-medium">A relay (or endpoint)</span> is a place to send audio and video from your streaming device and have it displayed into broadcasting software like OBS. With only an endpoint, you still need to provide hardware with OBS to receive the data and push it out to your desired streaming platform.
      <br /><br />
      <span className="text-purple-400 font-medium">OBS Hosting</span> is renting a computer in a data centre that's running 24/7 ready to accept your streaming data and send it out to your choice of streaming platform. <span className="text-cyan-300">No additional hardware required.</span>
      <br /><br />
      All IRLhosting OBS servers come with a minimum of <span className="text-cyan-400">1 relay (endpoint)</span> and connection monitoring and <span className="text-purple-400">BRB protection</span> software.
    </>,
  },
  {
    question: "I want multiple cameras/drones, what plan is correct for me?",
    answer: <>For users who occasionally stream using drones, we recommend the <span className="text-purple-400 font-medium">Professional Plan</span>, designed to support multiple endpoints effectively.
      <br /><br />
      If you have any questions or need guidance on hardware compatibility, please join our <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Discord community</a>. Our team is always here to assist and find the best solution tailored for you.</>,
  },
  {
    question: "What platforms do you support?",
    answer: <>We proudly support a wide range of platforms, including <span className="text-purple-400">Twitch.tv</span>, <span className="text-cyan-400">YouTube</span>, <span className="text-blue-400">Facebook</span>, <span className="text-green-400">Kick.com</span>, <span className="text-pink-400">TikTok</span>, <span className="text-cyan-300">Afreecatv</span>, and many others.
      <br /><br />
      Essentially, if there's a service you can stream to, <span className="text-cyan-400 font-medium">we've got you covered!</span></>,
  },
  {
    question: "What is Cloud OBS and why do I need it?",
    answer: <><span className="text-cyan-400 font-medium">Cloud OBS</span> is a cloud-based version of Open Broadcaster Software. This allows you to manage your stream remotely without needing any additional hardware.
      <br /><br />
      By running on cloud servers instead of your personal computer, it provides <span className="text-purple-400">higher-quality streams</span>. This reduces the risk of interruptions due to home network issues, eliminates frequent hardware upgrades and maintenance, offers <span className="text-cyan-400">enhanced security</span>, and gives you access to a <span className="text-purple-400">support team of experienced streamers</span>.</>,
  },
  {
    question: "Do you partner with streamers?",
    answer: <><span className="text-cyan-400 font-medium">Yes!</span> We offer custom-tailored hosting packages for qualified streamers who maintain consistent viewership and conduct regular IRL streams.
      <br /><br />
      Partners receive a <span className="text-purple-400">specialized hosting solution</span> designed specifically for their needs, along with <span className="text-cyan-400">premium support</span> and exclusive features. Visit our <Link to="/partners" className="text-cyan-400 hover:text-cyan-300 underline">Partners page</Link> to learn more.</>,
  },
  {
    question: "Will you help set everything up?",
    answer: <><span className="text-cyan-400 font-medium">Absolutely!</span> We provide assistance for basic setup across all packages.
      <br /><br />
      However, if you opt for the <span className="text-purple-400 font-medium">Professional package</span>, you'll benefit from our <span className="text-cyan-400">white-glove setup service</span>. Join our <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Discord</a> for immediate support.</>,
  },
  {
    question: 'How long does setup take?',
    answer: <>Servers are deployed within <span className="text-cyan-400 font-medium">24 hours</span> of payment.
      <br /><br />
      Our team will configure your server and provide simple instructions to connect your encoder. We also offer <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Discord support</a> if you need help getting started.</>,
  },
  {
    question: 'How does disconnect protection work?',
    answer: <>When your mobile connection drops, your stream <span className="text-cyan-400 font-medium">stays live</span> on our server.
      <br /><br />
      Viewers see a custom holding scene (like <span className="text-purple-400">"Reconnecting..."</span>) instead of the stream going offline. Once you reconnect, the stream seamlessly continues.
      <br /><br />
      This <span className="text-cyan-400">prevents viewer loss</span> and keeps your VOD intact.</>,
  },
  {
    question: 'What is IRLremote+?',
    answer: <><span className="text-cyan-400 font-medium">IRLremote+</span> is our mobile app included with all plans.
      <br /><br />
      It lets you control your stream from your phone: <span className="text-purple-400">switch scenes</span>, <span className="text-cyan-400">toggle sources</span>, <span className="text-purple-400">check stream health</span>, and manage your broadcast without needing a laptop.</>,
  },
    {
    question: 'How do I get support?',
    answer: <>Our primary support channel is <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Discord</a> where our team and community are available to help. For account issues, you can also <a href="mailto:support@irlhosting.com" className="text-purple-400 hover:text-purple-300 underline">email us</a>.
      <br /><br />
      We pride ourselves on <span className="text-cyan-400 font-medium">fast, helpful responses</span> from people who actually understand IRL streaming.</>,
  },
];

export function FAQ() {
  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <SEO 
        title="FAQ"
        description="FAQ about IRL Hosting cloud OBS and relay services. Learn about SRT/SRTLA/RIST relays, disconnect protection, Belabox & Moblin setup, streaming platforms, and 24/7 support for IRL streamers."
        canonical="/faq"
      />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg"
          >
            Everything you need to know about IRL Hosting.
          </motion.p>
        </div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-gray-900/50 border border-gray-800 rounded-xl px-6 data-[state=open]:border-gray-700"
              >
                <AccordionTrigger className="text-left text-white hover:text-cyan-400 hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Still have questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-gray-900/50 border border-gray-800 rounded-2xl p-8"
        >
          <h2 className="text-xl font-semibold text-white mb-3">
            Still have questions?
          </h2>
          <p className="text-gray-400 mb-6">
            Our team is happy to help. Join our Discord community for fast support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://discord.gg/irltools" target="_blank" rel="noopener noreferrer">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
                Join Discord
              </Button>
            </a>
            <a href="mailto:support@irlhosting.com">
              <Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                Email Support
              </Button>
            </a>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link to="/pricing">
            <Button
              size="lg"
              variant="ghost"
              className="text-cyan-400 hover:text-cyan-300 hover:bg-transparent"
            >
              View Pricing Plans
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
